/**
 * The landing queue between a stream and the graph — and the reason a run keeps
 * moving in a background tab.
 *
 * A pass's nodes arrive on the provider's clock. The drains in `reason.ts` feed
 * them through this queue so arrival pacing is ours: one node per `beat`, slow
 * enough that a person can follow each strand landing. That beat is choreography
 * — it exists for a watcher and for nothing else. Nothing downstream depends on
 * the gaps; the graph is identical whether the nodes land over six seconds or in
 * one microtask.
 *
 * Which is why the pacing is CONDITIONAL, and that condition is this file's whole
 * reason to exist. Browsers throttle timers in hidden tabs: to once a second at
 * best, and in Chrome — after five minutes hidden, for timer chains five deep,
 * which a per-node drain is almost immediately — to once a MINUTE. A drain that
 * paced unconditionally therefore stalled every pass by minutes the moment the
 * tab lost the foreground, and an autonomous run, whose premise is that nobody
 * is present, slowed to the point of looking stopped precisely when its person
 * stepped away. The model had long since answered; the money was spent; the
 * nodes sat in this queue waiting for wake-ups the browser would not grant, so
 * the beat — a courtesy to a watcher — was holding up the run for a watcher who
 * demonstrably was not there.
 *
 * Two decisions follow, and both are load-bearing:
 *
 *  - **The beat applies only while the document is visible.** A hidden tab lands
 *    each node the moment it is dequeued. The accepted cost is that nodes landed
 *    while hidden appear already settled when the person returns — there is no
 *    unfurl to watch, which is what the timeline replay is for.
 *  - **Waiting for the next node is a wake, never a poll.** The old drain slept
 *    40ms between looks at the queue, and in a hidden tab even a zero-length
 *    timer is a once-a-minute wake — polling cannot be made safe by shortening
 *    it. So an idle drain parks on a promise that `push` and `close` resolve
 *    directly, which costs nothing to hold open and fires on the stream's own
 *    events, which background throttling does not touch.
 *
 * The check is per-iteration, so a tab hidden mid-pass stops pacing on the next
 * node and a tab revealed mid-pass resumes it — the person who comes back while
 * a stream is still running gets the choreography for whatever is left.
 */

/** The seam a stream feeds and a pass awaits. */
export interface Lander<T> {
  /** Queue one item for landing. */
  push(item: T): void
  /** No more items are coming; the drain finishes the queue and resolves. */
  close(): void
  /** Settled when everything pushed before `close` has landed. */
  done: Promise<void>
}

/**
 * Whether anyone could be watching the canvas right now.
 *
 * `document` is absent in the node test environment, and an environment with no
 * document has no hidden state to be in — the suites that stub one set
 * `visibilityState` explicitly.
 */
export const watched = (): boolean =>
  typeof document === 'undefined' || document.visibilityState !== 'hidden'

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

/**
 * Start a drain that lands queued items one at a time, pacing only for a
 * watcher.
 *
 * `pace` is read per item rather than captured, because the tempo control can
 * move mid-pass and the next strand should land at the speed the person just
 * chose.
 */
export function startLander<T>(land: (item: T) => void, pace: () => number): Lander<T> {
  const queue: T[] = []
  let open = true
  let wake: (() => void) | null = null
  // Null BEFORE calling, so a `push` from inside a resolved continuation finds
  // the slot empty and the next wait installs a fresh resolver.
  const kick = () => {
    const w = wake
    wake = null
    w?.()
  }
  const done = (async () => {
    while (open || queue.length) {
      if (queue.length) {
        land(queue.shift()!)
        if (watched()) await sleep(pace())
      } else {
        // The resolver is installed synchronously inside the constructor, so no
        // push can slip between the length check above and the wait — there is
        // no interleaving point for it to slip through.
        await new Promise<void>((r) => {
          wake = r
        })
      }
    }
  })()
  return {
    push: (item) => {
      queue.push(item)
      kick()
    },
    close: () => {
      // The flag first, then the kick: a drain woken by this call must re-read
      // the world and see it already closed.
      open = false
      kick()
    },
    done,
  }
}
